/**
 * Per-employee standing deductions resolver (#23305) — self-fetching hook,
 * mirroring `use_data_centre.ts`'s pattern: uses the real bearer client
 * (`getTrpcClient()`) and `useOAuthEntityContext()`. Three procedures:
 * - `payroll.listDeductions({ firm_id, entity_id, employee_id })` — read
 * - `payroll.addDeduction(...)` — create (no update; remove + re-add instead)
 * - `payroll.removeDeduction({ firm_id, entity_id, deduction_id })` — delete
 *
 * All three are already OAuth-allowlisted (`payroll:employer:read`/`write`).
 * No mock fallback — same stance as `use_data_centre.ts`: failures surface
 * as explicit states, never fabricated data.
 */
import * as React from 'react';
import { getTrpcClient, isPayrollAddonRequiredError } from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';

export type DeductionType = 'tax' | 'superannuation' | 'court_order' | 'other';

/** Shape returned by `payroll.listDeductions` — mirrors the `employee_deductions` DB row. */
export interface EmployeeDeductionRecord {
  id: string;
  employee_id: string;
  deduction_type: DeductionType;
  /** Minor units (cents) when `is_percentage=false`; basis points when `is_percentage=true`. */
  amount_cents_or_bps: number;
  is_percentage: boolean;
  effective_from: string;
  effective_to: string | null;
  reference: string | null;
}

/** Input fields for `payroll.addDeduction`. */
export interface AddDeductionInput {
  deduction_type: DeductionType;
  /** Integer cents (fixed) or basis points (percentage). See docs/money-rules.md. */
  amount_cents_or_bps: number;
  is_percentage: boolean;
  effective_from: string;
  effective_to?: string;
  reference?: string;
}

export type DeductionsLoadFailure = 'entitlement_required' | 'error';

export type DeductionsState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; deductions: EmployeeDeductionRecord[] };

export interface EmployeeDeductionsFetchInput {
  firm_id: string;
  entity_id: string;
  employee_id: string;
}

/** Raw shape from the backend — `deduction_type` is `string` from the SQLite column, not a narrowed union. */
export interface RawDeductionRecord extends Omit<
  EmployeeDeductionRecord,
  'deduction_type'
> {
  deduction_type: string;
}

export interface EmployeeDeductionsFetchDeps {
  listDeductions: (
    input: EmployeeDeductionsFetchInput,
  ) => Promise<{ deductions: RawDeductionRecord[] }>;
}

function defaultFetchDeps(): EmployeeDeductionsFetchDeps {
  const client = getTrpcClient();
  return {
    listDeductions: (input) => client.payroll.listDeductions.query(input),
  };
}

function toEmployeeDeductionRecord(
  raw: RawDeductionRecord,
): EmployeeDeductionRecord {
  return raw as EmployeeDeductionRecord;
}

export type FetchDeductionsResult =
  | { outcome: 'success'; deductions: EmployeeDeductionRecord[] }
  | { outcome: 'failure'; failure: DeductionsLoadFailure };

function classifyFailure(err: unknown): DeductionsLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

export async function fetchEmployeeDeductions(
  input: EmployeeDeductionsFetchInput,
  deps?: EmployeeDeductionsFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<FetchDeductionsResult> {
  try {
    const resolvedDeps = deps ?? defaultFetchDeps();
    const result = await resolvedDeps.listDeductions(input);
    return {
      outcome: 'success',
      deductions: result.deductions.map(toEmployeeDeductionRecord),
    };
  } catch (err) {
    if (!isCancelled()) {
      console.error('[wagepro-web] Failed to load employee deductions:', err);
    }
    return { outcome: 'failure', failure: classifyFailure(err) };
  }
}

export function resolveDeductionsState(input: {
  entityContext: OAuthEntityContext;
  deductions: EmployeeDeductionRecord[] | undefined;
  failure: DeductionsLoadFailure | undefined;
}): DeductionsState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.deductions !== undefined) {
    return { status: 'ready', deductions: input.deductions };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

export type AddDeductionResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: DeductionsLoadFailure };

export type RemoveDeductionResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: DeductionsLoadFailure };

export interface EmployeeDeductionsMutationDeps {
  addDeduction: (
    input: {
      firm_id: string;
      entity_id: string;
      employee_id: string;
    } & AddDeductionInput,
  ) => Promise<{ deduction_id: string }>;
  removeDeduction: (input: {
    firm_id: string;
    entity_id: string;
    deduction_id: string;
  }) => Promise<unknown>;
}

function defaultMutationDeps(): EmployeeDeductionsMutationDeps {
  const client = getTrpcClient();
  return {
    addDeduction: (input) => client.payroll.addDeduction.mutate(input),
    removeDeduction: (input) => client.payroll.removeDeduction.mutate(input),
  };
}

export async function addEmployeeDeduction(
  entityInput: { firm_id: string; entity_id: string; employee_id: string },
  deduction: AddDeductionInput,
  deps?: EmployeeDeductionsMutationDeps,
): Promise<AddDeductionResult> {
  try {
    const resolvedDeps = deps ?? defaultMutationDeps();
    await resolvedDeps.addDeduction({ ...entityInput, ...deduction });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] Failed to add employee deduction:', err);
    if (isPayrollAddonRequiredError(err)) {
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    return { outcome: 'failure', failure: 'error' };
  }
}

export async function removeEmployeeDeduction(
  entityInput: { firm_id: string; entity_id: string },
  deductionId: string,
  deps?: EmployeeDeductionsMutationDeps,
): Promise<RemoveDeductionResult> {
  try {
    const resolvedDeps = deps ?? defaultMutationDeps();
    await resolvedDeps.removeDeduction({
      firm_id: entityInput.firm_id,
      entity_id: entityInput.entity_id,
      deduction_id: deductionId,
    });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] Failed to remove employee deduction:', err);
    if (isPayrollAddonRequiredError(err)) {
      return { outcome: 'failure', failure: 'entitlement_required' };
    }
    return { outcome: 'failure', failure: 'error' };
  }
}

export interface UseEmployeeDeductionsResult {
  state: DeductionsState;
  deductions: EmployeeDeductionRecord[];
  is_loading: boolean;
  retry: () => void;
  add_deduction:
    | ((input: AddDeductionInput) => Promise<AddDeductionResult>)
    | undefined;
  remove_deduction:
    | ((deductionId: string) => Promise<RemoveDeductionResult>)
    | undefined;
}

/** Fetches and manages standing deductions for a single employee. */
export function useEmployeeDeductions(
  employeeId: string | null,
): UseEmployeeDeductionsResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const [deductions, setDeductions] = React.useState<
    EmployeeDeductionRecord[] | undefined
  >(undefined);
  const [failure, setFailure] = React.useState<
    DeductionsLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready' || employeeId === null) return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    const empId: string = employeeId;
    let cancelled = false;
    setDeductions(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchEmployeeDeductions(
        { firm_id: firmId, entity_id: entityId, employee_id: empId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setDeductions(result.deductions);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, employeeId, reloadToken]);

  const state = React.useMemo(
    () => resolveDeductionsState({ entityContext, deductions, failure }),
    [entityContext, deductions, failure],
  );

  const readyEntity =
    state.status === 'ready' && entityContext.status === 'ready'
      ? entityContext
      : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const add_deduction = canWrite
    ? async (input: AddDeductionInput): Promise<AddDeductionResult> => {
        if (employeeId === null)
          return { outcome: 'failure', failure: 'error' };
        const result = await addEmployeeDeduction(
          {
            firm_id: readyEntity.firm_id,
            entity_id: readyEntity.entity_id,
            employee_id: employeeId,
          },
          input,
        );
        if (result.outcome === 'success') {
          setReloadToken((t) => t + 1);
        }
        return result;
      }
    : undefined;

  const remove_deduction = canWrite
    ? async (deductionId: string): Promise<RemoveDeductionResult> => {
        const result = await removeEmployeeDeduction(
          {
            firm_id: readyEntity.firm_id,
            entity_id: readyEntity.entity_id,
          },
          deductionId,
        );
        if (result.outcome === 'success') {
          setReloadToken((t) => t + 1);
        }
        return result;
      }
    : undefined;

  return {
    state,
    deductions: state.status === 'ready' ? state.deductions : [],
    is_loading: state.status === 'loading',
    retry: () => {
      setReloadToken((t) => t + 1);
    },
    add_deduction,
    remove_deduction,
  };
}
